import React, {useState} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import moment from 'moment';
import {CalendarDays, ChevronRight} from 'lucide-react';
import {api} from '../config/api';
import secureStorage from '../utils/secureStorage';
import {apiDateToWallClock} from '../utils/dateHelpers';
import {statusLabel, statusStyle} from '../calander/calendarColors';
import {ListEmpty, ListLoader, Modal, Panel} from '../stock/StockUI';
import Button from '../components/Common/Button';

const PAGE_SIZE = 100; // the API's maximum per page

// The client's whole history, most recent first (upcoming appointments on top).
const fetchClientAppointments = async (clientId, page) => {
    const account = await secureStorage.getItem('selectedAccount');
    const {data} = await api.get('/evenements', {
        headers: {'X-account-id': account.id, Accept: 'application/json'},
        params: {client_id: clientId, sort_by: 'start', sort_order: 'DESC', page, itemsPerPage: PAGE_SIZE},
    });
    return {
        items: (data?.data || []).map((event) => ({
            ...event,
            startDate: apiDateToWallClock(event.start),
            endDate: apiDateToWallClock(event.end),
        })),
        pagination: data?.pagination || {},
    };
};

const StatusBadge = ({status}) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusStyle(status || 'confirmed').pill}`}>
        {statusLabel(status || 'confirmed')}
    </span>
);

const formatDuration = (start, end) => {
    const minutes = moment(end).diff(start, 'minutes');
    if (!(minutes > 0)) return null;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    if (!hours) return `${rest} min`;
    return rest ? `${hours} h ${String(rest).padStart(2, '0')}` : `${hours} h`;
};

const DetailRow = ({label, children}) => (
    <div className="flex justify-between gap-6 px-6 py-3">
        <dt className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">{label}</dt>
        <dd className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right min-w-0 break-words">{children}</dd>
    </div>
);

const AppointmentDetailModal = ({event, onClose}) => {
    const services = (event.service || '').split(',').map((s) => s.trim()).filter(Boolean);
    const duration = formatDuration(event.startDate, event.endDate);
    // The tablets write the note in `remarque`, same reading as the calendar
    const note = event.remarque || event.notes;

    return (
        <Modal title="Détails du rendez-vous" subtitle={event.title} onClose={onClose} bodyClass="">
            <dl className="divide-y divide-gray-200 dark:divide-gray-700">
                <DetailRow label="Statut"><StatusBadge status={event.status}/></DetailRow>
                <DetailRow label="Date">{moment(event.startDate).format('dddd DD MMMM YYYY')}</DetailRow>
                <DetailRow label="Horaire">
                    {moment(event.startDate).format('HH:mm')} – {moment(event.endDate).format('HH:mm')}
                    {duration && <span className="text-gray-500 dark:text-gray-400 font-normal"> ({duration})</span>}
                </DetailRow>
                <DetailRow label="Employé">{event.employee?.name || '—'}</DetailRow>
                <DetailRow label={services.length > 1 ? 'Services' : 'Service'}>
                    {services.length > 0 ? (
                        <span className="flex flex-wrap justify-end gap-1.5">
                            {services.map((service) => (
                                <span key={service} className="px-2 py-0.5 rounded-md text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                                    {service}
                                </span>
                            ))}
                        </span>
                    ) : '—'}
                </DetailRow>
                {event.location && <DetailRow label="Lieu">{event.location}</DetailRow>}
                {event.source === 'web' && <DetailRow label="Origine">Réservation en ligne</DetailRow>}
            </dl>
            {note && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Note</p>
                    <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">{note}</p>
                </div>
            )}
        </Modal>
    );
};

const ClientAppointments = ({clientId}) => {
    const [selected, setSelected] = useState(null);
    const {data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage} = useInfiniteQuery({
        queryKey: ['client-appointments', clientId],
        queryFn: ({pageParam}) => fetchClientAppointments(clientId, pageParam),
        initialPageParam: 1,
        getNextPageParam: ({pagination}) =>
            pagination.currentPage < pagination.totalPages ? pagination.currentPage + 1 : undefined,
        enabled: !!clientId,
    });
    // Appointments at the same time can come back on two pages: keep one of each
    const appointments = data && [...new Map(data.pages.flatMap((page) => page.items).map((e) => [e.id, e])).values()];
    const total = data?.pages[0].pagination.totalItems ?? appointments?.length;

    return (
        <Panel title="Historique des rendez-vous" aside={appointments && (
            <span className="text-xs text-gray-500 dark:text-gray-400">{total} rendez-vous</span>
        )}>
            {isLoading ? (
                <ListLoader label="Chargement des rendez-vous..."/>
            ) : isError ? (
                <p role="alert" className="text-center py-6 text-sm text-red-600 dark:text-red-400">
                    Impossible de charger les rendez-vous
                </p>
            ) : appointments.length === 0 ? (
                <ListEmpty icon={CalendarDays} title="Aucun rendez-vous"
                           text="Ce client n'a encore aucun rendez-vous."/>
            ) : (
                <>
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                    {appointments.map((event) => (
                        <li key={event.id}>
                            <button type="button" onClick={() => setSelected(event)}
                                    className="w-full text-left flex items-center justify-between gap-3 px-4 sm:px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                <span className="min-w-0">
                                    <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                                        {moment(event.startDate).format('DD MMM YYYY')} · {moment(event.startDate).format('HH:mm')} – {moment(event.endDate).format('HH:mm')}
                                    </span>
                                    <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {[event.service, event.employee?.name].filter(Boolean).join(' · ') || event.title}
                                    </span>
                                </span>
                                <span className="flex items-center gap-2 flex-shrink-0">
                                    <StatusBadge status={event.status}/>
                                    <ChevronRight className="w-4 h-4 text-gray-400"/>
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
                {hasNextPage && (
                    <div className="flex justify-center p-3 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="tinted" size="sm" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                            Afficher plus
                        </Button>
                    </div>
                )}
                </>
            )}
            {selected && <AppointmentDetailModal event={selected} onClose={() => setSelected(null)}/>}
        </Panel>
    );
};

export default ClientAppointments;
